import { useEffect, useState } from "react";
import { ClipboardText, Check, MagnifyingGlass } from "@phosphor-icons/react";
import { listProducts, createInventoryCount, listInventoryCounts } from "@/lib/api";
import { toast } from "sonner";

export default function Inventory() {
  const [products, setProducts] = useState([]);
  const [counts, setCounts] = useState({}); // productId -> counted value
  const [q, setQ] = useState("");
  const [history, setHistory] = useState([]);
  const [user, setUser] = useState(() => localStorage.getItem("stock_user") || "");

  const load = async () => {
    const [p, h] = await Promise.all([listProducts({ q }), listInventoryCounts()]);
    setProducts(p);
    setHistory(h);
  };
  useEffect(() => { load(); }, [q]); // eslint-disable-line

  const submit = async (pid) => {
    const val = counts[pid];
    if (val === undefined || val === "") { toast.error("Saisir une quantité"); return; }
    try {
      await createInventoryCount({ product_id: pid, counted: Number(val), user: user || null });
      toast.success("Comptage enregistré");
      setCounts((c) => ({ ...c, [pid]: "" }));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  return (
    <div className="fade-up space-y-6">
      <header>
        <div className="text-xs uppercase tracking-[0.15em] font-bold text-[color:var(--text-muted)]">Contrôle</div>
        <h1 className="text-4xl font-heading font-medium mt-1 flex items-center gap-3">
          <ClipboardText size={36} weight="duotone" className="text-[color:var(--brand)]" /> Inventaire
        </h1>
        <p className="text-[color:var(--text-muted)] mt-1">Saisissez la quantité réelle comptée. L'écart est enregistré et le stock ajusté automatiquement.</p>
      </header>

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <MagnifyingGlass size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-[color:var(--text-muted)]" />
          <input
            data-testid="inv-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher un produit à inventorier…"
            className="w-full h-12 pl-11 pr-4 rounded-2xl border border-[color:var(--border-soft)] bg-white focus:outline-none focus:ring-2 focus:ring-[color:var(--brand)]"
          />
        </div>
        <input
          data-testid="inv-user"
          value={user}
          onChange={(e) => { setUser(e.target.value); localStorage.setItem("stock_user", e.target.value); }}
          placeholder="Votre nom"
          className="h-12 px-4 rounded-2xl border border-[color:var(--border-soft)] bg-white focus:outline-none min-w-[180px]"
        />
      </div>

      <div className="bg-white rounded-3xl border border-[color:var(--border-soft)] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[color:var(--brand-muted)]">
              <tr className="text-left">
                <th className="py-3 px-4 text-xs uppercase font-bold tracking-wider text-[color:var(--text-muted)]">Produit</th>
                <th className="py-3 px-4 text-xs uppercase font-bold tracking-wider text-[color:var(--text-muted)]">Stock théorique</th>
                <th className="py-3 px-4 text-xs uppercase font-bold tracking-wider text-[color:var(--text-muted)]">Compté</th>
                <th className="py-3 px-4 text-xs uppercase font-bold tracking-wider text-[color:var(--text-muted)]">Écart</th>
                <th className="py-3 px-4"></th>
              </tr>
            </thead>
            <tbody>
              {products.map((p) => {
                const counted = counts[p.id] === undefined ? "" : counts[p.id];
                const delta = counted !== "" ? Number(counted) - p.stock : null;
                return (
                  <tr key={p.id} className="border-t border-[color:var(--border-soft)]" data-testid={`inv-row-${p.id}`}>
                    <td className="py-3 px-4">
                      <div className="font-medium">{p.name}</div>
                      <div className="text-xs text-[color:var(--text-muted)]">{p.category} · {p.unit}</div>
                    </td>
                    <td className="py-3 px-4 tabular-nums font-semibold">{p.stock}</td>
                    <td className="py-3 px-4">
                      <input
                        data-testid={`inv-count-${p.id}`}
                        type="number"
                        value={counted}
                        onChange={(e) => setCounts((c) => ({ ...c, [p.id]: e.target.value }))}
                        className="w-28 h-12 px-3 text-center rounded-xl border border-[color:var(--border-soft)] focus:outline-none focus:ring-2 focus:ring-[color:var(--brand)]"
                      />
                    </td>
                    <td className="py-3 px-4 tabular-nums">
                      {delta === null ? <span className="text-[color:var(--text-muted)]">—</span> :
                        <span className={`font-semibold ${delta === 0 ? "text-[color:var(--brand)]" : delta > 0 ? "text-amber-700" : "text-red-700"}`}>
                          {delta > 0 ? "+" : ""}{delta}
                        </span>}
                    </td>
                    <td className="py-3 px-4">
                      <button
                        onClick={() => submit(p.id)}
                        disabled={counted === ""}
                        className="h-12 px-4 rounded-xl bg-[color:var(--brand)] text-white flex items-center gap-2 press-scale hover:bg-[color:var(--brand-hover)] disabled:opacity-40"
                        data-testid={`inv-save-${p.id}`}
                      >
                        <Check size={18} weight="bold" /> Valider
                      </button>
                    </td>
                  </tr>
                );
              })}
              {products.length === 0 && <tr><td colSpan={5} className="py-14 text-center text-[color:var(--text-muted)]">Aucun produit.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <section className="bg-white rounded-3xl border border-[color:var(--border-soft)] p-6">
        <h2 className="font-heading text-lg font-medium mb-3">Historique inventaires</h2>
        {history.length === 0 ? (
          <div className="text-[color:var(--text-muted)] text-center py-4">Aucun comptage pour l'instant.</div>
        ) : (
          <ul className="space-y-2 max-h-96 overflow-y-auto" data-testid="inv-history">
            {history.map((h) => (
              <li key={h.id} className="flex items-center gap-3 p-3 rounded-2xl border border-[color:var(--border-soft)]">
                <div className="flex-1">
                  <div className="font-medium">{h.product_name}</div>
                  <div className="text-xs text-[color:var(--text-muted)]">{new Date(h.created_at).toLocaleString("fr-FR")} {h.user && `· ${h.user}`}</div>
                </div>
                <div className="text-sm text-[color:var(--text-muted)]">Théo : <b>{h.expected}</b> → Compté : <b>{h.counted}</b></div>
                <div className={`font-semibold tabular-nums ${h.delta === 0 ? "text-[color:var(--brand)]" : h.delta > 0 ? "text-amber-700" : "text-red-700"}`}>
                  {h.delta > 0 ? "+" : ""}{h.delta}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
